import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import * as React from 'react'

import {LargeYSpace, SmallYSpace} from '../common/space'
import {
  PartnerInfo,
  filterPartnerInfo,
  usePartnerInfo,
} from '../common/partner-info'
import {isNumber} from '../common/isNumber'
import {isCardanoAddress} from '../common/isCardanoAddress'
import {Links} from '@yoroi/types'
import {requestAdaWithLink} from 'api/requestAdaWithLink'

export const TransferRequestAdaWithLink = () => {
  const [scheme, setScheme] = React.useState<
    Links.YoroiUriConfig['scheme'] | ''
  >('')
  const [exchangeLink, setExchangeLink] = React.useState('')
  const [address, setAddress] = React.useState('')
  const [amount, setAmount] = React.useState('')

  const [schemaError, setSchemeError] = React.useState(false)
  const [addressError, setAddressError] = React.useState(false)
  const [amountError, setAmountError] = React.useState(false)
  const [linkError, setLinkError] = React.useState(false)

  const partnerInfo = usePartnerInfo()

  const noSchema = scheme === ''
  const noAddress = address === ''
  const noAmount = amount === '' || !isNumber(amount)

  const disabled = noSchema || noAddress || noAmount

  const onGenerateLink = React.useCallback(() => {
    if (noSchema) setSchemeError(true)
    if (noAddress) setAddressError(true)
    if (noAmount) setAmountError(true)
    if (disabled) return

    const filteredPartnerInfo = filterPartnerInfo(partnerInfo)

    const {link, error} = requestAdaWithLink({
      scheme,
      address,
      amount,
      partnerInfo: filteredPartnerInfo,
    })

    if (link && !error) {
      if (linkError) setLinkError(false)
      setExchangeLink(link)
      return
    }

    setLinkError(true)
  }, [
    address,
    amount,
    disabled,
    linkError,
    noAddress,
    noAmount,
    noSchema,
    partnerInfo,
    scheme,
  ])

  return (
    <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', p: 1}}>
      <LargeYSpace />

      <FormControl>
        <InputLabel id="scheme-label">Schema</InputLabel>

        <Select
          sx={{width: '200px'}}
          labelId="scheme-label"
          id="scheme"
          value={scheme}
          label="Schema"
          variant="outlined"
          onChange={(event) => {
            setScheme(event.target.value as Links.YoroiUriConfig['scheme'])
            setSchemeError(false)
          }}
          error={schemaError}
        >
          <MenuItem value="yoroi">yoroi</MenuItem>

          <MenuItem value="https">https</MenuItem>
        </Select>
      </FormControl>

      <SmallYSpace />

      <TextField
        onChange={(event) => {
          setAddress(event.target.value)
          setAddressError(false)
        }}
        id="address"
        label="Address"
        variant="outlined"
        value={address}
        error={addressError}
        helperText={
          addressError && !isCardanoAddress(address) && 'Not a Cardano address'
        }
      />

      <SmallYSpace />

      <TextField
        onChange={(event) => {
          setAmount(event.target.value)
          setAmountError(false)
        }}
        sx={{width: '200px'}}
        id="amount"
        label="Amount"
        variant="outlined"
        value={amount}
        error={amountError}
        helperText={amountError && !isNumber(amount) && 'Not a number'}
      />

      <LargeYSpace />

      <PartnerInfo {...partnerInfo} />

      <LargeYSpace />

      <Button
        onClick={onGenerateLink}
        sx={{width: '150px'}}
        variant="contained"
        disabled={disabled}
      >
        Generate Link
      </Button>

      <LargeYSpace />

      <Divider />

      <LargeYSpace />

      <Typography sx={{fontSize: 18, fontWeight: 600}}>Link:</Typography>

      {linkError ? (
        <Typography sx={{color: 'red'}}>Error generating link</Typography>
      ) : (
        <Typography sx={{wordBreak: 'break-word'}}>{exchangeLink}</Typography>
      )}

      <LargeYSpace />

      <Button
        onClick={() => {
          navigator.clipboard.writeText(exchangeLink)
        }}
        sx={{width: '150px'}}
        variant="contained"
        disabled={exchangeLink === ''}
      >
        Copy
      </Button>

      <LargeYSpace />

      <Button
        onClick={() => {
          window.location = exchangeLink as unknown as Location
        }}
        sx={{width: '150px'}}
        variant="contained"
        disabled={exchangeLink === ''}
      >
        Open
      </Button>
    </Box>
  )
}
